import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { setUser, updateUser } from 'store/slices/user-slice';

import type { Establishment, Establishments, Region, User } from './types';

const BASE_URL = process.env.REACT_APP_ENDPOINT as string;

export const userApi = createApi({
	reducerPath: 'userApi',
	keepUnusedDataFor: 0,
	baseQuery: fetchBaseQuery({
		baseUrl: `${BASE_URL}`,
		prepareHeaders: (headers) => {
			const token = localStorage.getItem('userToken');
			if (token) {
				headers.set('authorization', `Bearer ${token}`);
			}
			return headers;
		},
	}),

	tagTypes: ['User', 'PatientCards', 'Establishments'],

	endpoints: (builder) => ({
		infoUser: builder.query<User, null>({
			query() {
				return {
					url: '/api/user/info',
					method: 'GET',
					credentials: 'include',
				};
			},

			providesTags: (result) => (result ? [{ type: 'User', id: result.id }] : ['User']),
		}),

		updateUser: builder.mutation<User, Partial<User>>({
			query: (queryArg) => ({
				url: '/api/user',
				method: 'POST',
				body: queryArg,
				credentials: 'include',
			}),

			async onQueryStarted(userData, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(updateUser(data));
				} catch (error) {
					console.error('Ошибка обновления данных пользователя:', error);
				}
			},
			invalidatesTags: (error) => (error ? [] : [{ type: 'User' }]),
		}),

		adminUpdateUser: builder.mutation<User, { id: number | undefined; body: Partial<User> }>({
			query: ({ id, body }) => ({
				url: `/api/admin/update-user/${id}`,
				method: 'PATCH',
				body: body,
				credentials: 'include',
			}),

			async onQueryStarted(userData, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(updateUser(data));
				} catch (error) {
					console.error('Ошибка обновления данных пользователя:', error);
				}
			},
			invalidatesTags: (error) => (error ? [] : [{ type: 'User' }]),
		}),

		getEstablishments: builder.query<Establishments, any>({
			query: ({ pageNumber, pageSize, sort, sortField, chars }) => ({
				url: '/api/public/establishments',
				method: 'GET',
				params: { pageNumber, pageSize, sort, sortField, chars },
				credentials: 'include',
			}),
			providesTags: (result) => ['Establishments'],
		}),

		addEstablishment: builder.mutation<null, Establishment>({
			query(queryArg) {
				return {
					url: '/api/establishment/upsert',
					method: 'POST',
					body: queryArg,
				};
			},
			invalidatesTags: ['Establishments'],
		}),

		getRegions: builder.query<Region[], void>({
			query: () => ({
				url: '/api/public/regions',
				method: 'GET',
				credentials: 'include',
			}),
		}),

		uploadAvatar: builder.mutation<string, { id: number; photo: File }>({
			query: ({ id, photo }) => {
				const formData = new FormData();
				formData.append('photo', photo);

				return {
					url: `/api/user/upload-avatar/${id}`,
					method: 'POST',
					body: formData,
					credentials: 'include',
					responseHandler: (response) => response.text(),
				};
			},

			async onQueryStarted({ id, photo }, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;

					if (typeof data === 'string') {
						console.log('Аватар успешно обновлен. Ссылка на изображение:', data);
					} else {
						console.log('Ожидалась ссылка на изображение, но получен другой ответ:', data);
					}
				} catch (error) {
					if (error instanceof Error) {
						console.error('Ошибка при загрузке аватара:', error.message);
					} else {
						console.error('Неизвестная ошибка при загрузке аватара:', error);
					}
				}
			},
			invalidatesTags: (result, error, { id }) => {
				console.log('invalidatesTags - User id:', id);
				return [{ type: 'User', id }];
			},
		}),

		getPrivacyPolicy: builder.query<Blob, void>({
			query: () => ({
				url: 'api/user/privacy-policy',
				method: 'GET',
				responseHandler: (response) => response.blob(),
			}),

			serializeQueryArgs: () => 'downloadFile',
		}),

		getUserAgreement: builder.query<Blob, void>({
			query: () => ({
				url: '/api/user/user-agreement',
				method: 'GET',
				responseHandler: (response) => response.blob(),
			}),

			serializeQueryArgs: () => 'downloadFile',
		}),

		getPersonalDataAgreement: builder.query<Blob, void>({
			query: () => ({
				url: '/api/user/consent-personal-data',
				method: 'GET',
				responseHandler: (response) => response.blob(),
			}),

			serializeQueryArgs: () => 'downloadFile',
		}),
	}),
});

export const {
	useInfoUserQuery,
	useUpdateUserMutation,
	useAdminUpdateUserMutation,
	useUploadAvatarMutation,
	useGetEstablishmentsQuery,
	useAddEstablishmentMutation,
	useGetRegionsQuery,
	useGetPrivacyPolicyQuery,
	useGetUserAgreementQuery,
	useGetPersonalDataAgreementQuery,
} = userApi;
